<template lang="pug">
    Tabs.booking-list
        .app-hero
            .app-hero-text
                p.app-hero-label Scanner-App
                h3.app-hero-title Jetzt mit dem Smartphone scannen
                p.app-hero-sub Öffnet die QR-Code Scanner-App
                a.app-hero-url(:href="appUrl" target="_blank" rel="noopener") {{ appUrl }}
            .app-hero-qr
                canvas(ref="appQrCanvas")

        el-card.contains-table( shadow="always" )
            .error(v-if="error")
                template(v-if="error.code")
                    Error(:message="$t(error.message || 'Something went wrong!')" :code="error.code")
                template(v-else)
                    Error(:message="$t('Something went wrong!')" :code="501")
            .bookings-table(v-else)
                // :row-link-config="bookingsPaginated.link"
                paginated-items(
                    scope="ex"
                    type="evnt_booking"
                    :loading="loading"
                    :items="bookingsPaginated.items"
                    :filters="filters"
                    :show-filters="showFilters"
                    :variables="bookingsPaginated.variables"
                    :all-columns="bookingsPaginated.allColumns"
                    :active-columns="bookingsPaginated.activeColumns"
                    :page-info="bookingsPaginated.pageInfo"
                    :sort="sort"
                    @pagination-size-change="handleSizeChange"
                    @pagination-current-change="handleCurrentChange"
                    @show-table-columns="handleColsShow"
                    @change-filter="handleFilterInputChange"
                    @change-sort="handleSortInputChange"
                    @click="handleClick"
                )
                    div(slot="top")
                        el-row.table-top(v-loading="loading" type="flex" justify="space-between")
                            .left
                                el-button(type="primary" size="mini" icon="el-icon-fa-plus fas" @click="addBooking")
                                    | {{ $t('New Booking') }}
                            .right.hide-on-empty
                                el-button(
                                    plain
                                    size="mini"
                                    type="primary"
                                    icon="el-icon-fa-download fas"
                                    :disabled="!eventOptions.length"
                                    @click="exportDialogVisible = true"
                                )
                                    | {{ $t('Export') }}
                                el-button(
                                    plain
                                    size="mini"
                                    type="primary"
                                    icon="el-icon-fa-filter fas"
                                    @click="toggleFilterVisiblity"
                                    :disabled="disable.showFilters || hasFilters"
                                )
                                    span(v-if="showFilters") {{ $t('Hide filters') }}
                                    span(v-else) {{ $t('Show filters') }}
                    template(v-slot:scanned="{ item }")
                        template(v-if="item.scanned")
                            el-tag(
                                type="success"
                                size="mini"
                                disable-transitions
                            ) {{ $t('Gescannt') }}
                            span.scanned-at(v-if="item.scanned_at") {{ scannedAtLabel(item) }}
                        el-tag(
                            v-else
                            type="info"
                            size="mini"
                            disable-transitions
                        ) {{ $t('Nicht gescannt') }}

        //- Export: Veranstaltung waehlen, dann Spalten und PDF-Optionen
        bookings-export-dialog(
            :visible="exportDialogVisible"
            :events="eventOptions"
            :event-id="exportEventId"
            :login-options="appLoginOptions"
            :login-email="appLoginEmail"
            @update:loginEmail="email => appLoginEmail = email"
            @export-pdf="openBookingsPdf"
            @close="exportDialogVisible = false"
        )

        //- PDF-Vorschau (serverseitig gerendert)
        bookings-pdf-drawer(
            :visible="pdfDrawerVisible"
            :event-id="pdfEventId"
            :columns="pdfSelectedColumns"
            :sort="pdfSort"
            :landscape="pdfLandscape"
            :show-qr-code="pdfShowQrCode"
            :login-email="appLoginEmail"
            @close="pdfDrawerVisible = false"
        )
</template>

<i18n>
{
    "de": {
        "New Booking": "Neue Anmeldung",
        "Export": "Exportieren",
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "Booking list could not be fetched.": "Buchungsliste konnte nicht abgerufen werden."
    }
}
</i18n>

<script>
import _ from 'lodash'
import QRCode from 'qrcode'
import { adminClient as apolloClient } from '@/store/apollo'
import { useSetupStore, useEventManagerBookingStore } from '@/stores'

import router from '@/router'
import pinia from '@/pinia';

import EventApi from '@/extensions/eventManager/store/api/event'
import { fetchAppLoginOptions } from '@/extensions/eventManager/utils/appLoginOptions'

import PaginatedItems from '@/components/PaginatedItems'
import Tabs from '@/extensions/eventManager/views/Partials/Tabs'
import BookingsExportDialog from '@/extensions/eventManager/views/Partials/BookingsExportDialog'
import BookingsPdfDrawer from '@/extensions/eventManager/views/Event/Form/Partials/BookingsPdfDrawer'
import Error from '@/components/Error';
import { timestamp2date } from '@/filters';

export default {
    name: 'Bookings',
    data() {
        return {
            error: false,
            disable: {
                showFilters: false,
            },
            // Export: waehlbare Veranstaltungen und die Einstellungen des Dialogs
            exportDialogVisible: false,
            exportEventId: null,
            eventOptions: [],
            pdfDrawerVisible: false,
            pdfEventId: null,
            pdfSelectedColumns: [],
            pdfSort: 'name',
            pdfLandscape: false,
            pdfShowQrCode: true,
            // Team-Zugaenge des Webauftritts fuer die Vorbelegung der App-Anmeldung
            appLoginOptions: [],
            appLoginEmail: '',
        }
    },
    components: {
        PaginatedItems,
        BookingsExportDialog,
        BookingsPdfDrawer,
        Error,
        Tabs
    },
    watch: {
        activeCms(cms) {
            const reload = _.get(cms, 'reloadComponents', true)
            if (reload) this.initialize()
        },
    },
    computed: {
        showFilters() {
            return this.hasFilters || _.get(this.bookingsPaginated, 'showFilters') || false
        },
        hasFilters() {
            return ! _.isEmpty(this.filters)
        },
        filters() {
            let filters = _.cloneDeep(this.bookingsPaginated.filters)
            delete filters.booking_key
            delete filters.cms
            return filters
        },
        appUrl() {
            return `${window.location.origin}/app`
        },
        setupStore() {
            return useSetupStore(pinia);
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        debounce() {
            return useSetupStore().debounce;
        },
        bookingStore() {
            return useEventManagerBookingStore();
        },
        loading() {
            return this.bookingStore.loading;
        },
        bookingsPaginated() {
            return this.bookingStore.bookingsPaginated;
        },
        sort() {
            var variables = this.bookingsPaginated.variables;
            var defaultSort = this.bookingsPaginated.defaultSort || { field: 'id', direction: 'asc' };
            var order = (variables.order !== undefined && variables.order !== '') ? variables.order : defaultSort.field;
            var direction = (variables.direction !== undefined && variables.direction !== '') ? variables.direction : defaultSort.direction;
            return { field: order, direction: direction };
        }
    },
    methods: {
        scannedAtLabel(item) {
            const ts = _.get(item, 'scanned_at')
            return ts ? timestamp2date(ts, 'dd.MM.yyyy HH:mm') + ' Uhr' : ''
        },
        drawAppQr() {
            if (!this.$refs.appQrCanvas) return
            QRCode.toCanvas(this.$refs.appQrCanvas, this.appUrl, {
                width: 120,
                margin: 1,
                color: { dark: '#000000', light: '#ffffff' },
            })
        },
        initialize() {
            this.updateLoading(true)
            this.refetch()
            this.loadExportOptions()
        },
        // Veranstaltungen und Team-Zugaenge fuer den Export-Dialog laden
        loadExportOptions() {
            const cmsID = _.get(this.activeCms, 'id')

            EventApi.fetchList(cmsID)
                .then(events => {
                    this.eventOptions = _.orderBy(events || [], ['name'], ['asc'])
                    if (!this.exportEventId) this.exportEventId = _.get(this.eventOptions, '0.id', null)
                })
                .catch(() => this.eventOptions = [])

            fetchAppLoginOptions(cmsID).then(options => this.appLoginOptions = options)
        },
        // Anmeldeliste der gewaehlten Veranstaltung als PDF anzeigen
        openBookingsPdf(settings) {
            this.pdfEventId = settings.eventId
            this.pdfSelectedColumns = settings.columns
            this.pdfSort = settings.sort
            this.pdfLandscape = settings.landscape
            this.pdfShowQrCode = settings.showQrCode
            this.pdfDrawerVisible = true
        },
        addBooking() {
            const params = { id: 'new' }
            router.push({ name: 'event-booking-main', params })
        },
        handleClick(item) {
            const params = { id: item.id }
            router.push({ name: 'event-booking-main', params })
        },
        handleSizeChange(itemsPerPage) {
            this.updateLoading(true)
            this.defineItemsPerPage(itemsPerPage)
            this.refetch()
        },
        handleCurrentChange (currentPage) {
            this.updateLoading(true)
            this.defineCurrentPage(currentPage)
            this.refetch()
        },
        handleColsShow(visibleColumns) {
            this.defineActiveColumns(visibleColumns)
            // apolloClient.cache.reset()
            apolloClient.clearStore()
        },
        handleFilterInputChange(key, value, debounce = true) {
            this.defineFilterValue({ key, value })
            if (debounce) this.debouncedRefetch()
            else this.refetch()
        },
        handleSortInputChange(key, value) {
            this.bookingStore.defineSorting({ key, value })
            this.refetch()
        },
        toggleFilterVisiblity() {
            const visible = this.showFilters

            let value = false
            if (! visible) value = true

            this.disable.showFilters = true
            this.defineFilterVisibility(value)
            this.saveFilterVisibility({ scope: 'ex', key: 'evnt_booking', value })
                .finally(() => this.disable.showFilters = false)
        },
        refetch() {

            // always add the cms id to filters
            const cmsID = this.activeCms.id
            const filter = {
                key: 'cms',
                match: cmsID,
            }
            this.defineFilterValue({ key: 'cms', value: filter })

            this.fetchBookingsPaginated()
                .then(() => this.updateLoading(false))
                .catch(err => {
                    const { graphQLErrors } = err

                    let code = 501;
                    let message = 'An error occurred!';

                    if (graphQLErrors) {
                        code = _.get(graphQLErrors, '0.code') || code;
                        message = _.get(graphQLErrors, '0.message') || message;
                    }

                    let notify = true;
                    switch (code) {
                        case 401:
                            notify = false;
                            this.error = { code, message: 'You are not authorized' };
                            break;
                        case 403:
                            notify = false;
                            this.error = { code, message: 'You are not allowed to be here' };
                            break;
                        // case 404:
                        //     break;
                        default:
                            this.error = true;
                            break;
                    }

                    if (notify) {
                        this.$notify.error({
                            title: this.$t(message),
                            message: this.$t('Booking list could not be fetched.'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                    }

                    this.updateLoading(false);

                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);
                })
        },
        debouncedRefetch: _.debounce(function() { // function scope is needed here
            this.updateLoading(true)
            this.refetch()
        }, useSetupStore(pinia).debounce || 300),
        saveFilterVisibility(payload) {
            return this.setupStore.saveFilterVisibility(payload);
        },
        activateTab(item) {
            return useSetupStore().activateTab(item)
        },
        fetchBookingsPaginated() {
            return this.bookingStore.fetchBookingsPaginated();
        },
        updateLoading(value) {
            this.bookingStore.updateLoading(value);
        },
        defineItemsPerPage(perPage) {
            this.bookingStore.defineItemsPerPage(perPage);
        },
        defineCurrentPage(currentPage) {
            this.bookingStore.defineCurrentPage(currentPage);
        },
        defineActiveColumns(columns) {
            this.bookingStore.defineActiveColumns(columns);
        },
        defineFilterValue(payload) {
            this.bookingStore.defineFilterValue(payload);
        },
        defineFilterVisibility(value) {
            this.bookingStore.defineFilterVisibility(value);
        },
    },
    mounted() {
        this.initialize()
        this.$nextTick(() => this.drawAppQr())
    },
}
</script>

<style lang="sass">
    .booking-list .scanned-at
        margin-left: 8px
        color: #909399
        font-size: 12px

    .error
        padding: 20px

    // Scanner-Banner: gleiches Farbschema wie im Event-Formular (Anmeldungen-Tab)
    .app-hero
        position: relative
        overflow: hidden
        display: flex
        align-items: center
        justify-content: space-between
        gap: 20px
        background: #fcfdff
        border: 1px solid $--border-color-lighter
        border-radius: 4px
        padding: 20px 24px
        margin-bottom: 0

        &::after
            content: ''
            position: absolute
            top: 0
            right: 0
            bottom: 0
            width: 55%
            background-image: radial-gradient($--color-primary-light-6 1.5px, transparent 1.5px)
            background-size: 12px 12px
            opacity: .35
            pointer-events: none
            // weiches Einsetzen statt harter Kante
            mask-image: linear-gradient(to right, transparent 0%, #000 65%)
            -webkit-mask-image: linear-gradient(to right, transparent 0%, #000 65%)

        .app-hero-text
            position: relative
            flex: 1
            min-width: 0

            .app-hero-label
                margin: 0 0 6px
                font-size: 11px
                font-weight: 600
                text-transform: uppercase
                letter-spacing: 1.5px
                color: $--color-primary

            .app-hero-title
                font-size: 18px
                font-weight: 700
                color: $--color-text-primary
                margin: 0 0 6px

            .app-hero-sub
                font-size: 13px
                color: $--color-text-regular
                margin: 0 0 10px

            .app-hero-url
                display: inline-block
                // padding: 2px 6px
                background: rgba(255, 255, 255, .75)
                border-radius: 3px
                font-family: monospace
                font-size: 11px
                color: $--color-text-secondary
                word-break: break-all
                text-decoration: none
                &:hover,
                &:focus
                    color: $--color-primary
                    text-decoration: underline

        .app-hero-qr
            position: relative
            z-index: 10
            flex-shrink: 0
            text-align: center
            background: $--color-white
            border: 1px solid $--color-primary-light-8
            border-radius: 4px
            padding: 10px
            box-shadow: 0 2px 10px rgba(0, 0, 0, .06)

            canvas
                display: block
</style>
